import { useEffect, useState, useMemo, useRef } from "react";
import "../../assets/css/Organization/ListOrganization.css";
import "../../assets/css/Common/stats_cards.css";
import api from "../../api/axios";
import OrganizationDrawer from "./OrganizationDrawer";
import OrganizationCreateModal from "./CreateOrganization";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import { LuBuilding2 } from "react-icons/lu";
import {
  FiUsers,
  FiBriefcase,
  FiTrendingUp,
} from "react-icons/fi";
import DynamicTable from "../Common/DynamicTable";
import { organizationColumns } from "./OrganizationColumns";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";

const DEFAULT_CARDS = {
  total_organizations: 0,
  active_organizations: 0,
  inactive_organizations: 0,
  active_deals: 0,
};

export default function OrganizationsList() {
  const [organizations, setOrganizations] = useState([]);
  const [cards, setCards] = useState(DEFAULT_CARDS);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [countryFilter, setCountryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (filter = dateFilter) => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = buildDateFilterParams(filter);
      const hasParams = Object.keys(params).length > 0;
      const [orgsRes, cardsRes] = await Promise.all([
        hasParams ? api.get("/organization/", { params }) : api.get("/organization/"),
        hasParams ? api.get("/organization/cards", { params }) : api.get("/organization/cards"),
      ]);

      if (currentRequestId !== requestIdRef.current) return;

      setOrganizations(Array.isArray(orgsRes.data) ? orgsRes.data : []);
      setCards(cardsRes.data || DEFAULT_CARDS);
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error("Failed to load organizations:", err);
      setOrganizations([]);
      setCards(DEFAULT_CARDS);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchOrganizations = (filter) => fetchData(filter);
  const fetchCards = (filter) => fetchData(filter);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    fetchData(newFilter);
  };




  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const filteredData = useMemo(() => {
    let result = organizations.filter((org) => {
      const safeSearch = (search || "").toLowerCase().trim();
      const matchesSearch =
        !safeSearch ||
        org.name?.toLowerCase().includes(safeSearch) ||
        org.website?.toLowerCase().includes(safeSearch) ||
        org.country?.toLowerCase().includes(safeSearch) ||
        org.state?.toLowerCase().includes(safeSearch) ||
        org.email?.toLowerCase().includes(safeSearch) ||
        org.phone?.toLowerCase().includes(safeSearch) ||
        org.status?.toLowerCase().includes(safeSearch) ||
        String(org.id || "").includes(safeSearch);

      const matchesCountry = countryFilter
        ? org.country === countryFilter
        : true;

      const matchesStatus = statusFilter
        ? org.status === statusFilter
        : true;

      return matchesSearch && matchesCountry && matchesStatus;
    });

    if (sortConfig.key) {
      result = [...result].sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (aVal == null) return 1;
        if (bVal == null) return -1;

        // Number sorting
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortConfig.direction === "asc"
            ? aVal - bVal
            : bVal - aVal;
        }

        // Date sorting
        if (
          sortConfig.key.toLowerCase().includes("date") ||
          sortConfig.key === "created_at"
        ) {
          return sortConfig.direction === "asc"
            ? new Date(aVal) - new Date(bVal)
            : new Date(bVal) - new Date(aVal);
        }

        // String sorting
        return sortConfig.direction === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }

    return result;
  }, [
    organizations,
    search,
    countryFilter,
    statusFilter,
    sortConfig,
  ]);



  const countries = useMemo(() => {
    return [...new Set(organizations.map((o) => o.country).filter(Boolean))];
  }, [organizations]);

  const statuses = useMemo(() => {
    return [...new Set(organizations.map((o) => o.status).filter(Boolean))];
  }, [organizations]);


  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredData.map((org) => org.id));
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) {
      Swal.fire("Select organizations first", "", "info");
      return;
    }

    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "Selected organizations will be deleted",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      await api.post("/organization/bulk-delete", {
        organization_ids: selectedIds,
      });

      Swal.fire("Deleted!", "", "success");

      setSelectedIds([]);
      fetchOrganizations();
    } catch (err) {
      Swal.fire("Error", "Delete failed", "error");
    }
  };

  const user = JSON.parse(sessionStorage.getItem("user"));
  const canDelete =
    user?.role !== "Sales Executive" &&
    user?.role !== "Sales Manager";


  const openOrganization = async (id) => {
    try {
      const res = await api.get(`/organization/${id}`);
      setSelectedOrganization(res.data);
      setDrawerOpen(true);
    } catch (err) {
      if (err.response?.status === 403) {
        // Silently do not open drawer, no error popup displayed
        return;
      }
      console.error("Failed to open organization:", err);
    }
  };

  return (
    <>
      <div className="main-wrapper">
        {/* Header */}
        <div className="page-header">
          <div>
            <h2>Organizations</h2>
          </div>
          <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
        </div>

        {/* Stats */}
        <div className={`stats-grid stats-grid-4${loading ? " refreshing" : ""}`}>
          <div className="stat-card">
            <div className="stat-icon blue">
              <LuBuilding2 />
            </div>
            <div>
              <p>Total Organizations</p>
              <h2>{cards?.total_organizations ?? 0}</h2>

            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <FiUsers />
            </div>
            <div>
              <p>Active Organizations</p>
              <h2>{cards?.active_organizations ?? 0}</h2>

            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon purple">
              <FiBriefcase />
            </div>
            <div>
              <p>Inactive Organizations</p>
              <h2>{cards?.inactive_organizations ?? 0}</h2>

            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon gold">
              <FiTrendingUp />
            </div>
            <div>
              <p>Active Deals</p>
              <h2>{cards?.active_deals ?? 0}</h2>

            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-card">
          <DynamicTable
            data={filteredData}
            columns={organizationColumns({
              selectedIds,
              handleSelect,
              handleSelectAll,
              totalRows: filteredData.length,
              canDelete,
            })}
            onRowClick={openOrganization}
            search={search}
            onSearchChange={setSearch}
            onAdd={() => setIsCreateOpen(true)}
            addButtonText="Add Organization"
            searchPlaceholder="Search organizations..."
            selectedIds={selectedIds}
            selectedCount={selectedIds.length}
            onBulkDelete={handleBulkDelete}
            storageKey="organization_columns"
            canDelete={canDelete}
            sortConfig={sortConfig}
            onSortChange={handleSort}
            filters={[
              {
                name: "status",
                value: statusFilter,
                onChange: setStatusFilter,
                options: statuses,
                placeholder: "All Status",
              },
            ]}
          />
        </div>
      </div>

      {/* Drawer */}
      <OrganizationDrawer
        organization={selectedOrganization}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        refreshOrganizations={fetchOrganizations}
      />

      {/* Create Modal */}
      {isCreateOpen && (
        <OrganizationCreateModal
          onClose={() => setIsCreateOpen(false)}
          refreshOrganizations={fetchOrganizations}
        />
      )}
    </>
  );
}